import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { buildTimesheetTourSteps, TourStep } from './timesheet-tour.steps';
import { emit as emitTour, subscribe, TourEvent } from './tour-event-bus';
import {
  getFieldState,
  resetFieldState,
  subscribeToFieldState,
  TourFieldKey,
} from './tour-field-state';
import { setCurrentTourStepKey } from './tour-step-state';

/* localStorage keys — kept in one place so the schema is easy to audit
   and change in future. */
const LS_KEY_STATE = 'timesheet-tour:state';
const LS_KEY_COMPLETED = 'timesheet-tour:completed';

interface PersistedState {
  active: boolean;
  step: number;
  hasAnyClient: boolean;
  /* Epoch ms — used to expire stale "in-progress" tours so a user who
     closed the tab a week ago doesn't get dropped back in mid-tour. */
  savedAt: number;
}

const RESUME_MAX_AGE_MS = 60 * 60 * 1000; // 1 hour

/* useTimesheetTour
   ----------------
   Centralised state for the Timesheet Guided Tour:
   - `isOpen`            — whether the reactour <Tour> is showing.
   - `steps`             — the resolved step list (2A vs 2B branch decided
                           at `start()` time from the current Redux state).
   - `currentStep`       — zero-based index into `steps`.
   - `start()`           — opens the tour from step 0.
   - `close()`           — closes + resets to step 0.
   - `setCurrentStep`    — direct setter; passed to reactour as
                           `getCurrentStep` callback so Next/Prev work.

   PHASE B (not yet wired into individual step components):
   Each step may declare a `gateOn` event from the tour bus. When that
   event fires while the matching step is active, the tour auto-advances
   to the next step. The subscription effect below is already in place. */
export interface TimesheetTourApi {
  isOpen: boolean;
  steps: TourStep[];
  currentStep: number;
  start: () => void;
  /** Pause the tour at the current step — used by × and Skip. The
   *  next `start()` resumes from this same step. */
  close: () => void;
  /** Fully end the tour — used by the Finish Tour button on the last
   *  step. Clears the persisted step so the next `start()` begins at
   *  Step 1 again. */
  finish: () => void;
  setCurrentStep: (i: number) => void;
  /** Move one step backwards (no-op at step 0). */
  back: () => void;
  /** Move one step forward (no-op past the last step). */
  next: () => void;
  /** Skip the whole tour — alias for `close()`; saved step is kept. */
  skip: () => void;
  /** True iff the user is on the first step. */
  isFirstStep: boolean;
  /** True iff the user is on the last step. */
  isLastStep: boolean;
  /** True iff the current step is event-gated (the user must perform an
   *  action; the Next button should be hidden on these steps). */
  isEventGated: boolean;
  /** True iff Next is allowed right now. View-only steps return true;
   *  predicate-gated steps return true only when their predicate is
   *  satisfied by the current field-state snapshot. */
  canAdvance: boolean;
  /** True iff the user has reached the final step at least once. Read
   *  from localStorage on mount so the page can show a "Replay tour"
   *  affordance differently from a "Take tour" affordance later. */
  hasCompleted: boolean;
}

export const useTimesheetTour = (): TimesheetTourApi => {
  // Read customer list from Redux so we can decide branch 2A vs 2B
  // when the tour starts. Selector is defensive — survives missing
  // slice shape during early renders.
  const customers = useSelector((state: any) => state?.customer?.customers ?? []);

  /* No useTranslation needed here — each step's `body` is a function
     `(t) => ReactNode` evaluated inside TourBubble (which has its own
     useTranslation), so the hook is language-agnostic. */

  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [steps, setSteps] = useState<TourStep[]>(() => buildTimesheetTourSteps(false));

  /* Field-state subscription. `fieldTick` is a counter that bumps on
     every push from the sidebar; it forces a re-render so derived
     values like `canAdvance` re-evaluate. We don't store the snapshot
     in state because the field-state module already holds it — copying
     would just give us two sources of truth. */
  const [fieldTick, setFieldTick] = useState(0);
  useEffect(() => {
    return subscribeToFieldState(() => setFieldTick((t) => t + 1));
  }, []);

  /* hasCompleted — read once from localStorage and kept in state so we
     can flip it from inside `close()` when the user reaches the last
     step without forcing a parent re-render. */
  const [hasCompleted, setHasCompleted] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    try { return window.localStorage.getItem(LS_KEY_COMPLETED) === 'true'; }
    catch { return false; }
  });

  /* Track whether the tour was resumed from localStorage. This affects
     whether close() should clear the persisted state (yes, normal flow)
     or skip clearing (no — used internally during a resume restore). */
  const resumedRef = useRef<boolean>(false);

  const start = useCallback(() => {
    const hasAnyClient = Array.isArray(customers) && customers.length > 0;
    /* Always start from Step 1. The saved-step state in localStorage
       is only consumed by the auto-resume on page refresh (so an
       accidental F5 doesn't lose the user's progress). Explicit
       Skip / × / Finish all clear the state below, so when the user
       clicks Start Tour again it begins fresh. */
    setSteps(buildTimesheetTourSteps(hasAnyClient));
    setCurrentStep(0);
    resetFieldState();
    if (typeof window !== 'undefined') {
      try { window.localStorage.removeItem(LS_KEY_STATE); } catch { /* ignore */ }
    }
    setIsOpen(true);
    // Lifecycle event — TimesheetCreation listens to expand any
    // collapsed weeks so the Step 11 / 13 selectors always resolve.
    emitTour('tour:opened');
  }, [customers]);

  /* (Language-change rebuild effect removed — each step's `body` is
     now a function `(t) => ReactNode` that's invoked inside TourBubble
     using the live useTranslation hook. So when the user switches
     language, the bubble re-renders and immediately picks up the new
     translation. No manual step rebuild needed.) */

  /* close — used by × and Skip Tour. Fully clears the persisted
     step so the next `start()` begins at Step 1. The auto-resume on
     page refresh is preserved separately — it only fires when the
     user closed the tab mid-tour, never after an explicit dismiss. */
  const close = useCallback(() => {
    setIsOpen(false);
    setCurrentStep(0);
    resetFieldState();
    if (typeof window !== 'undefined') {
      try { window.localStorage.removeItem(LS_KEY_STATE); } catch { /* ignore */ }
    }
    resumedRef.current = false;
    emitTour('tour:closed');
  }, []);

  /* finish — used by the Finish Tour button on the last step.
     Behaviourally identical to close(); kept as a separate name so
     consumer code reads more clearly (and so we can diverge later
     without touching call sites). */
  const finish = useCallback(() => {
    setIsOpen(false);
    setCurrentStep(0);
    resetFieldState();
    if (typeof window !== 'undefined') {
      try { window.localStorage.removeItem(LS_KEY_STATE); } catch { /* ignore */ }
    }
    resumedRef.current = false;
    emitTour('tour:closed');
  }, []);

  /* Linear navigation helpers — kept in the hook so the bubble UI
     stays a pure consumer of the api. Bounds-clamped so over-clicking
     Next on the last step or Back on the first step is a silent no-op. */
  const back = useCallback(() => {
    setCurrentStep((i) => (i > 0 ? i - 1 : i));
  }, []);
  const next = useCallback(() => {
    setCurrentStep((i) => i + 1);
  }, []);
  /* Skip = same as close but ALSO emits `'tour:skipped'`. The page
     subscribes to this event to show a confirmation toast + a brief
     pulse animation on the Start Tour button — reassuring the user
     that the tour can be replayed any time. */
  const skip = useCallback(() => {
    close();
    emitTour('tour:skipped');
  }, [close]);

  /* Mirror the live step key into the module-scope `tour-step-state`
     store so non-tour components (AddCustomerModal) can lock their
     close paths only when the tour is genuinely on the step that
     affects them. Cleared to null when the tour closes. */
  useEffect(() => {
    if (!isOpen) {
      setCurrentTourStepKey(null);
      return;
    }
    setCurrentTourStepKey(steps[currentStep]?.key ?? null);
    /* No teardown — the next isOpen flip handles cleanup. */
  }, [isOpen, currentStep, steps]);

  /* Phase B — auto-advance on bus events.
     Subscribes to every TourEvent that any step uses as a gate. When
     the event fires AND the current step's `gateOn` matches, advance.
     Other events are no-ops. Cleanly unsubscribes on unmount. */
  useEffect(() => {
    if (!isOpen) return;
    const active = steps[currentStep];
    if (!active?.gateOn) return;
    const event: TourEvent = active.gateOn;
    const unsub = subscribe(event, () => {
      // Guard against double-fires advancing more than once
      setCurrentStep((prev) => (prev === currentStep ? prev + 1 : prev));
    });
    return unsub;
  }, [isOpen, currentStep, steps]);

  // Derived flags — recomputed cheaply on every render, no need to memoise.
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === steps.length - 1;
  const isEventGated = Boolean(steps[currentStep]?.gateOn);

  /* Phase C — `canAdvance` reads the live field-state snapshot. View-
     only and event-gated steps always allow Next (event-gated already
     hide the button in the bubble UI). Predicate-gated steps return
     the predicate's verdict.
     The `fieldTick` dep makes this recompute after any field push. */
  const canAdvance = useMemo(() => {
    const active = steps[currentStep];
    if (!active?.predicate) return true;
    const snap: Partial<Record<TourFieldKey, unknown>> = {
      'tour:clientId':    getFieldState('tour:clientId'),
      'tour:taskDetails': getFieldState('tour:taskDetails'),
      'tour:fromDate':    getFieldState('tour:fromDate'),
      'tour:toDate':      getFieldState('tour:toDate'),
      'tour:pricingType': getFieldState('tour:pricingType'),
      'tour:price':       getFieldState('tour:price'),
    };
    return active.predicate(snap);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [steps, currentStep, fieldTick]);

  /* Phase C — persist state on every change while the tour is open.
     When the user reaches the final step, also flip the "completed"
     flag so we can offer a "Replay tour" affordance later. */
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!isOpen) return;
    const payload: PersistedState = {
      active: true,
      step: currentStep,
      hasAnyClient: steps.length === buildTimesheetTourSteps(true).length,
      savedAt: Date.now(),
    };
    try { window.localStorage.setItem(LS_KEY_STATE, JSON.stringify(payload)); }
    catch { /* quota / privacy mode — silently ignore */ }

    if (isLastStep && !hasCompleted) {
      setHasCompleted(true);
      try { window.localStorage.setItem(LS_KEY_COMPLETED, 'true'); }
      catch { /* ignore */ }
    }
  }, [isOpen, currentStep, steps, isLastStep, hasCompleted]);

  /* On mount, CLEAR any stale persisted tour state. The user's request:
     a page refresh should never auto-resume the tour. Every Start Tour
     click begins at Step 1 fresh — no exceptions. We still write to
     localStorage during the tour (the persist effect above) because
     the rest of the code reads it for diagnostics, but the auto-resume
     read on mount is now a CLEAR instead of a restore. */
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try { window.localStorage.removeItem(LS_KEY_STATE); } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return useMemo(
    () => ({
      isOpen, steps, currentStep,
      start, close, finish, setCurrentStep,
      back, next, skip,
      isFirstStep, isLastStep, isEventGated,
      canAdvance, hasCompleted,
    }),
    [isOpen, steps, currentStep, start, close, finish, back, next, skip, isFirstStep, isLastStep, isEventGated, canAdvance, hasCompleted]
  );
};
